import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  C, PageHeader, GlassCard, Badge, Button, StatusDot, ProgressBar, Modal, Skeleton, Input,
} from "@coremonit/design-system";
import {
  GitBranch, GitCommit, RefreshCw, RotateCcw, PlayCircle, GitPullRequestArrow,
  Boxes, Server, Clock, Diff, Search,
} from "lucide-react";
import { useDeployments, useDeploymentDiff, useRedeploy, useRestart, useRollback } from "../api/hooks";
import type { Deployment } from "@coremonit/types";

export function DeploymentsPage() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Deployment | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const deployments = useDeployments();
  const rollback = useRollback();
  const restart = useRestart();
  const redeploy = useRedeploy();
  const diff = useDeploymentDiff(diffOpen ? selected?.id : undefined);

  const items = useMemo(() => {
    const list = deployments.data?.items ?? [];
    if (!q.trim()) return list;
    const s = q.toLowerCase();
    return list.filter((d) => d.serviceName.toLowerCase().includes(s) || d.gitBranch.includes(s) || d.gitCommit.includes(s));
  }, [deployments.data, q]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<GitBranch size={16} />} title="Deployments" subtitle="GitOps-style view of every service — sync, health, rollout." />

      <GlassCard style={{ padding: 12 }}>
        <Input value={q} onChange={setQ} prefix={<Search size={13} />} placeholder="Search service, branch, commit…" />
      </GlassCard>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
        {deployments.isLoading
          ? Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={210} radius={16} />)
          : items.map((d) => (
              <DeploymentCard
                key={d.id}
                d={d}
                onSelect={() => setSelected(d)}
                onDiff={() => { setSelected(d); setDiffOpen(true); }}
                onRollback={() => rollback.mutateAsync(d.id).then((r) => toast.success(r.message))}
                onRestart={() => restart.mutateAsync(d.id).then(() => toast.success("Pods restarting"))}
                onRedeploy={() => redeploy.mutateAsync(d.id).then(() => toast.success("Redeploy queued"))}
              />
            ))}
      </div>

      {selected && (
        <DeploymentDetailModal
          open={!!selected && !diffOpen}
          onClose={() => setSelected(null)}
          d={selected}
          onDiff={() => setDiffOpen(true)}
          onRollback={() => rollback.mutateAsync(selected.id).then((r) => toast.success(r.message))}
          onRestart={() => restart.mutateAsync(selected.id).then(() => toast.success("Pods restarting"))}
          onRedeploy={() => redeploy.mutateAsync(selected.id).then(() => toast.success("Redeploy queued"))}
        />
      )}

      <Modal open={diffOpen} onClose={() => setDiffOpen(false)} title="Manifest diff" subtitle={selected ? `${selected.serviceName} · ${selected.previousRevision} → ${selected.revision}` : ""} size="lg">
        {diff.isLoading ? <Skeleton height={280} /> : (
          <pre style={{ margin: 0, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, fontSize: 12, color: C.text, overflow: "auto", fontFamily: "ui-monospace, Menlo, monospace" }}>
            {diff.data?.diff.map((l, i) => (
              <div key={i} style={{
                background: l.op === "+" ? "rgba(16,185,129,0.08)" : l.op === "-" ? "rgba(239,68,68,0.08)" : "transparent",
                color:      l.op === "+" ? C.green                   : l.op === "-" ? C.red                     : C.textSub,
                padding: "1px 6px", borderRadius: 4,
              }}>
                {l.op} {l.line}
              </div>
            ))}
          </pre>
        )}
      </Modal>
    </div>
  );
}

function DeploymentCard({ d, onSelect, onDiff, onRollback, onRestart, onRedeploy }: {
  d: Deployment; onSelect: () => void; onDiff: () => void;
  onRollback: () => void; onRestart: () => void; onRedeploy: () => void;
}) {
  const syncColor = d.syncStatus === "synced" ? C.green : d.syncStatus === "outofsync" ? C.amber : C.textMuted;
  const healthColor = d.healthStatus === "healthy" ? C.green : d.healthStatus === "warning" ? C.amber : d.healthStatus === "critical" ? C.red : C.textMuted;
  return (
    <GlassCard style={{ padding: 14 }} accent={syncColor + "40"}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <StatusDot status={d.healthStatus} pulse={d.rolloutPhase !== "healthy"} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.serviceName}</div>
            <div style={{ color: C.textMuted, fontSize: 10 }}>{d.revision} · {d.namespace} · {d.cluster}</div>
          </div>
        </div>
        <Badge color={d.syncStatus === "synced" ? "green" : "amber"}>{d.syncStatus}</Badge>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", color: C.textMuted, fontSize: 10, marginBottom: 4 }}>
          <span>Rollout</span><span style={{ color: healthColor, fontWeight: 700 }}>{d.rolloutProgressPct}%</span>
        </div>
        <ProgressBar value={d.rolloutProgressPct} color={healthColor} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 10, color: C.textSub, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><GitCommit size={10} /> {d.gitCommit}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><GitBranch size={10} /> {d.gitBranch}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Boxes size={10} /> {d.available}/{d.desired} pods</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={10} /> {new Date(d.createdAt).toLocaleString()}</div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <Button size="xs" variant="secondary" icon={<Diff size={11} />} onClick={onDiff}>Diff</Button>
        <Button size="xs" variant="ghost"     icon={<PlayCircle size={11} />} onClick={onRedeploy}>Redeploy</Button>
        <Button size="xs" variant="ghost"     icon={<RefreshCw size={11} />} onClick={onRestart}>Restart</Button>
        <Button size="xs" variant="danger"    icon={<RotateCcw size={11} />} onClick={onRollback}>Rollback</Button>
        <Button size="xs" variant="ghost" onClick={onSelect}>Details</Button>
      </div>
    </GlassCard>
  );
}

function DeploymentDetailModal({ open, onClose, d, onDiff, onRollback, onRestart, onRedeploy }: {
  open: boolean; onClose: () => void; d: Deployment;
  onDiff: () => void; onRollback: () => void; onRestart: () => void; onRedeploy: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} size="lg" title={<span>{d.serviceName} <Badge color="blue">{d.revision}</Badge></span>} subtitle={`${d.namespace} · ${d.cluster}`}
      footer={
        <>
          <Button variant="secondary" icon={<Diff size={11} />} onClick={onDiff}>Diff</Button>
          <Button variant="ghost" icon={<PlayCircle size={11} />} onClick={onRedeploy}>Redeploy</Button>
          <Button variant="ghost" icon={<RefreshCw size={11} />} onClick={onRestart}>Restart</Button>
          <Button variant="danger" icon={<RotateCcw size={11} />} onClick={onRollback}>Rollback</Button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <Info label="Git repo"    value={d.gitRepo} />
        <Info label="Branch"      value={d.gitBranch} />
        <Info label="Commit"      value={d.gitCommit} />
        <Info label="Image"       value={d.image} />
        <Info label="Sync status" value={d.syncStatus} color={d.syncStatus === "synced" ? C.green : C.amber} />
        <Info label="Health"      value={d.healthStatus} />
        <Info label="Triggered by"value={d.triggeredBy} />
        <Info label="Trigger"     value={d.triggerReason} />
      </div>

      <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Timeline · events</div>
      <div style={{ position: "relative", paddingLeft: 20 }}>
        <div style={{ position: "absolute", left: 6, top: 4, bottom: 4, width: 1, background: C.border }} />
        {d.events.map((e, i) => (
          <div key={i} style={{ display: "flex", gap: 12, marginBottom: 10, position: "relative" }}>
            <div style={{ position: "absolute", left: -20, top: 6, width: 12, height: 12, borderRadius: "50%", background: e.kind === "degraded" ? C.red : e.kind === "progressed" ? C.blue : C.green, boxShadow: `0 0 8px currentColor` }} />
            <div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{e.message}</div>
              <div style={{ color: C.textMuted, fontSize: 10 }}>{new Date(e.ts).toLocaleString()} · {e.kind}</div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Info({ label, value, color = C.text }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 12, marginTop: 3, wordBreak: "break-word", fontFamily: label === "Commit" ? "ui-monospace, monospace" : "inherit" }}>{value}</div>
    </div>
  );
}
